import { resolverValorDetallado } from '../motor/parser.js'
import { analizarBase64Imagen } from '../imagenes/base64.js'
import {
  comprobarRaiz,
  crearErrorDocx,
  elementosPorNombreLocal,
  parsearXmlDocx,
  serializarXmlDocx,
} from '../docx/xml.js'
import {
  obtenerRutaRelaciones,
  obtenerTargetRelativo,
  RELATIONSHIPS_NAMESPACE,
} from '../docx/partes.js'

const CONTENT_TYPES_PATH = '[Content_Types].xml'
const CONTENT_TYPES_NAMESPACE =
  'http://schemas.openxmlformats.org/package/2006/content-types'
const XMLNS_NAMESPACE = 'http://www.w3.org/2000/xmlns/'
const XML_NAMESPACE = 'http://www.w3.org/XML/1998/namespace'
const EMUS_POR_PIXEL = 9525

const NAMESPACES_TRANSICIONALES = {
  w: 'http://schemas.openxmlformats.org/wordprocessingml/2006/main',
  wp: 'http://schemas.openxmlformats.org/drawingml/2006/wordprocessingDrawing',
  a: 'http://schemas.openxmlformats.org/drawingml/2006/main',
  pic: 'http://schemas.openxmlformats.org/drawingml/2006/picture',
  r: 'http://schemas.openxmlformats.org/officeDocument/2006/relationships',
}

const NAMESPACES_ESTRICTOS = {
  w: 'http://purl.oclc.org/ooxml/wordprocessingml/main',
  wp: 'http://purl.oclc.org/ooxml/drawingml/wordprocessingDrawing',
  a: 'http://purl.oclc.org/ooxml/drawingml/main',
  pic: 'http://purl.oclc.org/ooxml/drawingml/picture',
  r: 'http://purl.oclc.org/ooxml/officeDocument/relationships',
}

const WORD_NAMESPACES = [NAMESPACES_TRANSICIONALES.w, NAMESPACES_ESTRICTOS.w]
const DRAWING_NAMESPACES = [NAMESPACES_TRANSICIONALES.wp, NAMESPACES_ESTRICTOS.wp]
const PLACEHOLDER_IMAGEN = /\$\{i:([^{}]+)\}/g

function crearElemento(documento, namespaceURI, qualifiedName, atributos = {}) {
  const elemento = documento.createElementNS(namespaceURI, qualifiedName)
  Object.entries(atributos).forEach(([nombre, valor]) => {
    elemento.setAttribute(nombre, String(valor))
  })
  return elemento
}

function agregarHijo(padre, hijo) {
  padre.appendChild(hijo)
  return hijo
}

function namespacesParaTexto(elementoTexto) {
  if (elementoTexto.namespaceURI === NAMESPACES_ESTRICTOS.w) return NAMESPACES_ESTRICTOS
  if (elementoTexto.namespaceURI === NAMESPACES_TRANSICIONALES.w) {
    return NAMESPACES_TRANSICIONALES
  }
  throw crearErrorDocx(
    'docx_word_namespace_unsupported',
    'El namespace WordprocessingML no está soportado',
    { namespaceURI: elementoTexto.namespaceURI }
  )
}

function construirDrawing(documento, elementoTexto, { cx, cy, drawingId, rId, imageName }) {
  const ns = namespacesParaTexto(elementoTexto)
  const prefijoWord = elementoTexto.prefix || 'w'
  const drawing = crearElemento(documento, ns.w, `${prefijoWord}:drawing`)
  const inline = agregarHijo(
    drawing,
    crearElemento(documento, ns.wp, 'wp:inline', {
      distT: 0,
      distB: 0,
      distL: 0,
      distR: 0,
    })
  )
  inline.setAttributeNS(XMLNS_NAMESPACE, 'xmlns:wp', ns.wp)
  inline.setAttributeNS(XMLNS_NAMESPACE, 'xmlns:a', ns.a)
  inline.setAttributeNS(XMLNS_NAMESPACE, 'xmlns:pic', ns.pic)
  inline.setAttributeNS(XMLNS_NAMESPACE, 'xmlns:r', ns.r)

  agregarHijo(inline, crearElemento(documento, ns.wp, 'wp:extent', { cx, cy }))
  agregarHijo(
    inline,
    crearElemento(documento, ns.wp, 'wp:effectExtent', { l: 0, t: 0, r: 0, b: 0 })
  )
  agregarHijo(
    inline,
    crearElemento(documento, ns.wp, 'wp:docPr', {
      id: drawingId,
      name: `Certiapp Image ${drawingId}`,
    })
  )

  const frameProperties = agregarHijo(
    inline,
    crearElemento(documento, ns.wp, 'wp:cNvGraphicFramePr')
  )
  agregarHijo(
    frameProperties,
    crearElemento(documento, ns.a, 'a:graphicFrameLocks', { noChangeAspect: 1 })
  )

  const graphic = agregarHijo(inline, crearElemento(documento, ns.a, 'a:graphic'))
  const graphicData = agregarHijo(
    graphic,
    crearElemento(documento, ns.a, 'a:graphicData', { uri: ns.pic })
  )
  const picture = agregarHijo(graphicData, crearElemento(documento, ns.pic, 'pic:pic'))
  const nonVisual = agregarHijo(
    picture,
    crearElemento(documento, ns.pic, 'pic:nvPicPr')
  )
  agregarHijo(
    nonVisual,
    crearElemento(documento, ns.pic, 'pic:cNvPr', { id: drawingId, name: imageName })
  )
  agregarHijo(nonVisual, crearElemento(documento, ns.pic, 'pic:cNvPicPr'))

  const blipFill = agregarHijo(picture, crearElemento(documento, ns.pic, 'pic:blipFill'))
  const blip = agregarHijo(blipFill, crearElemento(documento, ns.a, 'a:blip'))
  blip.setAttributeNS(ns.r, 'r:embed', rId)
  const stretch = agregarHijo(blipFill, crearElemento(documento, ns.a, 'a:stretch'))
  agregarHijo(stretch, crearElemento(documento, ns.a, 'a:fillRect'))

  const shapeProperties = agregarHijo(
    picture,
    crearElemento(documento, ns.pic, 'pic:spPr')
  )
  const transform = agregarHijo(
    shapeProperties,
    crearElemento(documento, ns.a, 'a:xfrm')
  )
  agregarHijo(transform, crearElemento(documento, ns.a, 'a:off', { x: 0, y: 0 }))
  agregarHijo(transform, crearElemento(documento, ns.a, 'a:ext', { cx, cy }))
  const geometry = agregarHijo(
    shapeProperties,
    crearElemento(documento, ns.a, 'a:prstGeom', { prst: 'rect' })
  )
  agregarHijo(geometry, crearElemento(documento, ns.a, 'a:avLst'))

  return drawing
}

function crearDocumentoRelaciones(partName) {
  return parsearXmlDocx(
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
      `<Relationships xmlns="${RELATIONSHIPS_NAMESPACE}"/>`,
    partName
  )
}

function obtenerContextoRelaciones(contexto, zip, partName) {
  if (contexto.relaciones.has(partName)) return contexto.relaciones.get(partName)

  const relsPath = obtenerRutaRelaciones(partName)
  const archivo = zip.file(relsPath)
  const documento = archivo
    ? parsearXmlDocx(archivo.asText(), relsPath)
    : crearDocumentoRelaciones(relsPath)
  const raiz = comprobarRaiz(documento, RELATIONSHIPS_NAMESPACE, 'Relationships', relsPath)
  const idsUsados = new Set(
    elementosPorNombreLocal(documento, RELATIONSHIPS_NAMESPACE, 'Relationship').map((elemento) =>
      elemento.getAttribute('Id')
    )
  )

  const relaciones = { documento, raiz, idsUsados, nextId: 1, relsPath }
  contexto.relaciones.set(partName, relaciones)
  return relaciones
}

function reservarRelationshipId(relaciones) {
  while (relaciones.idsUsados.has(`rId${relaciones.nextId}`)) relaciones.nextId++
  const rId = `rId${relaciones.nextId}`
  relaciones.nextId++
  relaciones.idsUsados.add(rId)
  return rId
}

function reservarDrawingId(contexto) {
  while (contexto.drawingIds.has(contexto.nextDrawingId)) contexto.nextDrawingId++
  const drawingId = contexto.nextDrawingId
  contexto.nextDrawingId++
  contexto.drawingIds.add(drawingId)
  return drawingId
}

function reservarNombreImagen(contexto, extension) {
  while (true) {
    const imageName = `certiapp-image-${contexto.nextMediaId}.${extension}`
    contexto.nextMediaId++
    const partName = `word/media/${imageName}`
    const clave = partName.toLowerCase()
    if (contexto.mediaPartNames.has(clave)) continue
    contexto.mediaPartNames.add(clave)
    return { imageName, partName }
  }
}

function registrarTipoContenido(contexto, partName, extension, mimeType) {
  const defaults = elementosPorNombreLocal(
    contexto.contentTypesDocument,
    CONTENT_TYPES_NAMESPACE,
    'Default'
  )
  const existente = defaults.find(
    (elemento) => elemento.getAttribute('Extension').toLowerCase() === extension.toLowerCase()
  )

  if (existente?.getAttribute('ContentType') === mimeType) return

  if (!existente) {
    const elemento = crearElemento(
      contexto.contentTypesDocument,
      CONTENT_TYPES_NAMESPACE,
      'Default',
      { Extension: extension, ContentType: mimeType }
    )
    contexto.contentTypesRoot.appendChild(elemento)
    contexto.contentTypesChanged = true
    return
  }

  const partNameAbsoluto = `/${partName}`
  const overrides = elementosPorNombreLocal(
    contexto.contentTypesDocument,
    CONTENT_TYPES_NAMESPACE,
    'Override'
  )
  const overrideExistente = overrides.find(
    (elemento) => elemento.getAttribute('PartName') === partNameAbsoluto
  )

  if (overrideExistente) {
    if (overrideExistente.getAttribute('ContentType') !== mimeType) {
      throw crearErrorDocx(
        'docx_content_type_conflict',
        'La imagen generada entra en conflicto con un tipo de contenido existente',
        { partName, mimeType, existingContentType: overrideExistente.getAttribute('ContentType') }
      )
    }
    return
  }

  const override = crearElemento(
    contexto.contentTypesDocument,
    CONTENT_TYPES_NAMESPACE,
    'Override',
    { PartName: partNameAbsoluto, ContentType: mimeType }
  )
  contexto.contentTypesRoot.appendChild(override)
  contexto.contentTypesChanged = true
}

function obtenerNodosTexto(documento) {
  return WORD_NAMESPACES.flatMap((namespaceURI) =>
    elementosPorNombreLocal(documento, namespaceURI, 't')
  )
}

function obtenerDrawingIds(zip, partes) {
  const ids = new Set()
  for (const partName of partes) {
    const documento = parsearXmlDocx(zip.file(partName).asText(), partName)
    for (const namespaceURI of DRAWING_NAMESPACES) {
      for (const elemento of elementosPorNombreLocal(documento, namespaceURI, 'docPr')) {
        const valor = elemento.getAttribute('id')
        if (/^\d+$/.test(valor)) ids.add(Number(valor))
      }
    }
  }
  return ids
}

export function crearContextoImagenes(zip, partes, placeholdersOriginalesPorParte = new Map()) {
  const archivoContentTypes = zip.file(CONTENT_TYPES_PATH)
  if (!archivoContentTypes) {
    throw crearErrorDocx(
      'docx_content_types_missing',
      'El paquete DOCX no contiene [Content_Types].xml'
    )
  }

  const contentTypesDocument = parsearXmlDocx(
    archivoContentTypes.asText(),
    CONTENT_TYPES_PATH
  )
  const contentTypesRoot = comprobarRaiz(
    contentTypesDocument,
    CONTENT_TYPES_NAMESPACE,
    'Types',
    CONTENT_TYPES_PATH
  )
  const mediaPartNames = new Set(
    Object.keys(zip.files)
      .filter((partName) => /^word\/media\/[^/]+$/i.test(partName))
      .map((partName) => partName.toLowerCase())
  )

  return {
    contentTypesDocument,
    contentTypesRoot,
    contentTypesChanged: false,
    mediaPartNames,
    nextMediaId: 1,
    drawingIds: obtenerDrawingIds(zip, partes),
    nextDrawingId: 1,
    relaciones: new Map(),
    placeholdersOriginalesPorParte,
  }
}

export function finalizarContextoImagenes(zip, contexto) {
  for (const relaciones of contexto.relaciones.values()) {
    zip.file(relaciones.relsPath, serializarXmlDocx(relaciones.documento))
  }
  if (contexto.contentTypesChanged) {
    zip.file(CONTENT_TYPES_PATH, serializarXmlDocx(contexto.contentTypesDocument))
  }
}

function parsearPlaceholderImagen(contenido, partName) {
  const partes = contenido.split(',').map((parte) => parte.trim())
  if (partes.length > 2 || !partes[0]) {
    return {
      ok: false,
      error: {
        code: 'image_placeholder_invalid',
        message: 'El placeholder de imagen tiene una sintaxis inválida',
        detail: { expression: contenido, partName },
      },
    }
  }

  let dimensiones = null
  if (partes.length === 2) {
    const match = partes[1].match(/^(\d+(?:\.\d+)?)x(\d+(?:\.\d+)?)$/i)
    const width = match ? Number(match[1]) : 0
    const height = match ? Number(match[2]) : 0
    if (!match || !Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
      return {
        ok: false,
        error: {
          code: 'image_size_invalid',
          message: 'Las dimensiones de imagen deben tener el formato anchoXalto y ser positivas',
          detail: { expression: contenido, size: partes[1], partName },
        },
      }
    }
    dimensiones = { width, height }
  }

  return { ok: true, path: partes[0], dimensiones }
}

function convertirDimensionEmu(valor) {
  const emus = Math.round(valor * EMUS_POR_PIXEL)
  return Number.isSafeInteger(emus) && emus > 0 ? emus : null
}

function crearNodoTexto(documento, original, contenido) {
  const nodo = original.cloneNode(false)
  nodo.appendChild(documento.createTextNode(contenido))
  if (/^\s|\s$/.test(contenido)) {
    nodo.setAttributeNS(XML_NAMESPACE, 'xml:space', 'preserve')
  }
  return nodo
}

function consumirPlaceholderOriginal(contexto, partName, normalizado) {
  const mapaParte = contexto.placeholdersOriginalesPorParte?.get(partName)
  const originales = mapaParte?.get(normalizado)
  return originales?.length ? originales.shift() : normalizado
}

function restaurarPlaceholdersPendientes(xml, contexto, partName) {
  const mapaParte = contexto.placeholdersOriginalesPorParte?.get(partName)
  if (!mapaParte) return xml

  let resultado = xml
  for (const [normalizado, originales] of mapaParte.entries()) {
    while (originales.length) {
      resultado = resultado.replace(normalizado, originales.shift())
    }
  }
  return resultado
}

function reemplazarNodoTexto(documento, nodoTexto, reemplazos) {
  const padre = nodoTexto.parentNode
  let cursor = 0

  for (const reemplazo of reemplazos) {
    if (reemplazo.start > cursor) {
      padre.insertBefore(
        crearNodoTexto(documento, nodoTexto, reemplazo.texto.slice(cursor, reemplazo.start)),
        nodoTexto
      )
    }
    padre.insertBefore(reemplazo.nodo, nodoTexto)
    cursor = reemplazo.end
  }

  if (cursor < nodoTexto.textContent.length) {
    padre.insertBefore(
      crearNodoTexto(documento, nodoTexto, nodoTexto.textContent.slice(cursor)),
      nodoTexto
    )
  }
  padre.removeChild(nodoTexto)
}

function errorImagenConContexto(error, path, partName) {
  return {
    ...error,
    detail: {
      ...(error.detail || {}),
      path,
      partName,
    },
  }
}

export function resolverImagenes(xml, datos, zip, partName, errors = [], contexto) {
  if (!contexto) {
    throw crearErrorDocx(
      'docx_image_context_missing',
      'No se proporcionó el contexto global de imágenes DOCX',
      { partName }
    )
  }

  if (!xml.includes('${i:')) return xml

  const documento = parsearXmlDocx(xml, partName)
  const grupos = []

  for (const nodoTexto of obtenerNodosTexto(documento)) {
    const texto = nodoTexto.textContent || ''
    if (!texto.includes('${i:')) continue

    const reemplazos = []
    const regex = new RegExp(PLACEHOLDER_IMAGEN.source, 'g')
    let match
    let textoCubierto = texto

    while ((match = regex.exec(texto)) !== null) {
      textoCubierto = textoCubierto.replace(match[0], '')
      const placeholderOriginal = consumirPlaceholderOriginal(contexto, partName, match[0])
      const placeholder = parsearPlaceholderImagen(match[1], partName)
      if (!placeholder.ok) {
        errors.push(placeholder.error)
        reemplazos.push({
          start: match.index,
          end: regex.lastIndex,
          texto,
          textoLiteral: placeholderOriginal,
        })
        continue
      }

      const valor = resolverValorDetallado(datos, placeholder.path)
      if (!valor.ok) {
        errors.push(errorImagenConContexto(valor.error, placeholder.path, partName))
        reemplazos.push({
          start: match.index,
          end: regex.lastIndex,
          texto,
          textoLiteral: placeholderOriginal,
        })
        continue
      }

      const analisis = analizarBase64Imagen(valor.value)
      if (!analisis.ok) {
        errors.push(errorImagenConContexto(analisis.error, placeholder.path, partName))
        reemplazos.push({
          start: match.index,
          end: regex.lastIndex,
          texto,
          textoLiteral: placeholderOriginal,
        })
        continue
      }

      const width = placeholder.dimensiones?.width || analisis.imagen.width
      const height = placeholder.dimensiones?.height || analisis.imagen.height
      const cx = convertirDimensionEmu(width)
      const cy = convertirDimensionEmu(height)
      if (cx === null || cy === null) {
        errors.push({
          code: 'image_size_out_of_range',
          message: 'Las dimensiones de imagen quedan fuera del rango representable en el DOCX',
          detail: { path: placeholder.path, width, height, partName },
        })
        reemplazos.push({
          start: match.index,
          end: regex.lastIndex,
          texto,
          textoLiteral: placeholderOriginal,
        })
        continue
      }

      reemplazos.push({
        start: match.index,
        end: regex.lastIndex,
        texto,
        path: placeholder.path,
        imagen: analisis.imagen,
        cx,
        cy,
      })
    }

    if (textoCubierto.includes('${i:')) {
      errors.push({
        code: 'image_placeholder_malformed',
        message: 'Se encontró un placeholder de imagen sin cierre o con llaves no válidas',
        detail: { text: texto, partName },
      })
    }

    if (reemplazos.length) grupos.push({ nodoTexto, reemplazos })
  }

  if (!grupos.length) return restaurarPlaceholdersPendientes(xml, contexto, partName)

  let relaciones = null
  for (const grupo of grupos) {
    for (const reemplazo of grupo.reemplazos) {
      if (reemplazo.textoLiteral !== undefined) {
        reemplazo.nodo = crearNodoTexto(
          documento,
          grupo.nodoTexto,
          reemplazo.textoLiteral
        )
        continue
      }

      if (!relaciones) relaciones = obtenerContextoRelaciones(contexto, zip, partName)
      const drawingId = reservarDrawingId(contexto)
      const rId = reservarRelationshipId(relaciones)
      const { imageName, partName: mediaPartName } = reservarNombreImagen(
        contexto,
        reemplazo.imagen.extension
      )

      registrarTipoContenido(
        contexto,
        mediaPartName,
        reemplazo.imagen.extension,
        reemplazo.imagen.mimeType
      )
      zip.file(mediaPartName, reemplazo.imagen.contenido, { base64: true })

      const relacion = crearElemento(
        relaciones.documento,
        RELATIONSHIPS_NAMESPACE,
        'Relationship',
        {
          Id: rId,
          Type: `${namespacesParaTexto(grupo.nodoTexto).r}/image`,
          Target: obtenerTargetRelativo(partName, mediaPartName),
        }
      )
      relaciones.raiz.appendChild(relacion)
      reemplazo.nodo = construirDrawing(documento, grupo.nodoTexto, {
        cx: reemplazo.cx,
        cy: reemplazo.cy,
        drawingId,
        rId,
        imageName,
      })
    }
    reemplazarNodoTexto(documento, grupo.nodoTexto, grupo.reemplazos)
  }

  return restaurarPlaceholdersPendientes(
    serializarXmlDocx(documento),
    contexto,
    partName
  )
}
